/**
 * Mitglieds-Auth-State.
 *
 * Token liegt in localStorage; `useMe` lädt das Profil über react-query, sobald
 * ein Token vorhanden ist. Bei 401 wird der Token verworfen → Komponenten
 * können sauber auf "ausgeloggt" reagieren.
 */

import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, getMemberToken, memberApi, setMemberToken } from "@/lib/api";
import type { MemberProfile } from "@/types/api";

const TOKEN_EVENT = "tanzraum.member.token.changed";

function emitTokenChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(TOKEN_EVENT));
  }
}

export function useMemberToken(): string | null {
  const [token, setToken] = useState<string | null>(() => getMemberToken());

  useEffect(() => {
    function refresh() {
      setToken(getMemberToken());
    }
    window.addEventListener(TOKEN_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(TOKEN_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  return token;
}

export function useMe() {
  const token = useMemberToken();
  const qc = useQueryClient();

  const query = useQuery<MemberProfile, ApiError>({
    queryKey: ["member", "me"],
    queryFn: memberApi.me,
    enabled: Boolean(token),
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (query.error?.status === 401) {
      setMemberToken(null);
      emitTokenChange();
      qc.removeQueries({ queryKey: ["member"] });
    }
  }, [query.error, qc]);

  return query;
}

export function useLogin() {
  const qc = useQueryClient();
  return useCallback(
    (accessToken: string) => {
      setMemberToken(accessToken);
      emitTokenChange();
      qc.invalidateQueries({ queryKey: ["member"] });
    },
    [qc],
  );
}

export function useLogout() {
  const qc = useQueryClient();
  return useCallback(() => {
    setMemberToken(null);
    emitTokenChange();
    qc.removeQueries({ queryKey: ["member"] });
  }, [qc]);
}
